define([
  'react',
  'prop-types',
  'underscore',
  'es6!sumo/util',
], (React, PropTypes, _, Util) => {
  const { classNames } = Util;

  const onPaste = (e) => {
    if (!e.clipboardData.getData('text/plain')) {
      e.preventDefault();
    }
  };

  class ContentEditable extends React.Component {
    static propTypes = {
      value: PropTypes.string,
      placeholder: PropTypes.string,
      onSubmit: PropTypes.func.isRequired,
      onChange: PropTypes.func.isRequired,
      disabled: PropTypes.bool,
      className: PropTypes.string,
    };

    static defaultProps = {
      value: '',
      placeholder: '',
      className: '',
      disabled: false,
    };

    constructor(props) {
      super(props);

      this.emitChange = this.emitChange.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.getPlaceholderStyles = this.getPlaceholderStyles.bind(this);

      this.state = {
        value: props.value,
        disabled: props.disabled,
      };
    }

    componentDidMount() {
      this.forceUpdate();
    }

    shouldComponentUpdate(nextProps, nextState) {
      this.placeholderEl.textContent = nextProps.placeholder;

      return (
        nextProps.value !== this.el.textContent ||
        nextProps.disabled !== this.props.disabled ||
        nextState.disabled !== this.state.disabled
      );
    }

    componentDidUpdate() {
      if (this.props.autoFocus) {
        setTimeout(() => { this.el.focus(); }, 50);
      }

      if (this.props.disabled !== this.state.disabled) {
        this.setState({ disabled: this.props.disabled });
      }

      if (this.props.value !== this.el.textContent) {
        this.el.textContent = this.props.value;

        if (this.placeholderEl) {
          if (this.props.value) {
            this.placeholderEl.style.display = 'none';
          } else {
            this.placeholderEl.style.display = 'block';
          }
        }
      }
    }

    onKeyDown(e) {
      const { onSubmit } = this.props;

      if (e.keyCode === 13 && onSubmit) {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();

        if (this.el) {
          setTimeout(() => {
            this.el.focus();
            this.emitChange();
          }, 10);
        }
      }
    }

    emitChange() {
      const { value: lastValue } = this.state;
      const value = this.el.textContent;

      if (this.props.onChange && value !== lastValue) {
        this.props.onChange(value);
      }

      this.setState({ value });

      const selection = window.getSelection();
      const offset = selection.anchorOffset;

      if (offset === value.length) {
        this.el.scrollTop = this.el.scrollHeight;
      }

      if (this.placeholderEl) {
        if (value) {
          this.placeholderEl.style.display = 'none';
        } else {
          this.placeholderEl.style.display = 'block';
        }
      }
    }

    getPlaceholderStyles() {
      const { value } = this.state;
      const { placeholder } = this.props;

      return {
        display: value ? 'none' : 'block',
      };

      if (!placeholder) return {};

      const style = {
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0.5,
        pointerEvents: 'none',
        display: value ? 'none' : 'block',
      };

      if (this.el) {
        const contentStyle = window.getComputedStyle(this.el);

        // Copy padding styles
        style.padding = contentStyle.getPropertyValue('padding');
        style.paddingTop = contentStyle.getPropertyValue('padding-top');
        style.paddingRight = contentStyle.getPropertyValue('padding-right');
        style.paddingBottom = contentStyle.getPropertyValue('padding-bottom');
        style.paddingLeft = contentStyle.getPropertyValue('padding-left');

        // Copy margin styles
        style.margin = contentStyle.getPropertyValue('margin');
        style.marginTop = contentStyle.getPropertyValue('margin-top');
        style.marginRight = contentStyle.getPropertyValue('margin-right');
        style.marginBottom = contentStyle.getPropertyValue('margin-bottom');
        style.marginLeft = contentStyle.getPropertyValue('margin-left');

        // Copy font styles
        style.font = contentStyle.getPropertyValue('font');

        // Copy dimension styles
        style.width = contentStyle.getPropertyValue('width');
        style.height = contentStyle.getPropertyValue('height');

        // Copy line height style
        style.lineHeight = contentStyle.getPropertyValue('line-height');
      }

      return style;
    }

    renderPlaceholder() {
      const { value } = this.state;
      const { placeholder } = this.props;
      if (!placeholder) return null;

      const style = this.getPlaceholderStyles();
      return (
        <div ref={(el) => { this.placeholderEl = el; }} style={style} className="sumo-content-editable-placeholder">
          {placeholder}
        </div>
      );
    }

    render() {
      const { value, disabled } = this.state;

      const className = classNames(this.props.className, {
        'sumo-content-editable-disabled': disabled,
      });

      return (
        <div style={{ position: 'relative' }}>
          <div
            key="contenteditable"
            suppressContentEditableWarning
            className={className}
            onInput={this.emitChange}
            onBlur={this.emitChange}
            onKeyDown={this.onKeyDown}
            onPaste={onPaste}
            ref={(el) => { this.el = el; }}
            contentEditable={!disabled}
          >
            {value}
          </div>
          {this.renderPlaceholder()}
        </div>
      );
    }
  }

  return ContentEditable;
});
